import { createContext, useContext } from 'react';

/**
 * The authentication context itself, apart from the provider that fills it (Phase 103).
 *
 * React Fast Refresh can only update a module in place when everything it exports is a component.
 * `auth-context.tsx` exported `useAuth` as well, so every edit to it — including edits to files it
 * imports — invalidated the module, and a reloaded module means a **new** context object while the
 * mounted tree still holds the old one. The whole application then fell into its error boundary
 * with "useAuth must be used inside an AuthProvider", which is a sentence about nesting describing
 * something that has nothing to do with nesting.
 *
 * It happens every few edits while somebody is working, and it happened to the owner of this
 * product, who reported it as a bug in the screen he was looking at.
 *
 * So the context and the hook live here, in a module with no component in it and almost no reason
 * to change, and the provider next door can be hot-reloaded as often as it likes.
 */

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  email_verified: boolean;
  is_platform_admin: boolean;
  active_tenant_id: string | null;
  role_key: string | null;
  /** Interface preferences; the server stores them and gives them back untouched. */
  preferences?: Record<string, unknown>;
  /** The language and zone this person reads the product in. */
  locale?: string;
  timezone?: string;
  /** Which notification kinds also arrive by e-mail, every kind resolved to a true or a false. */
  email_notifications?: Record<string, boolean>;
  push_notifications?: Record<string, boolean>;
  /** Null when the password has never been changed since the account was created. */
  password_changed_at?: string | null;
  /** Short-lived signed URL for the profile picture; null when there is not one. */
  avatar_url?: string | null;
}

export interface AuthContextValue {
  user: CurrentUser | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  login: (email: string, password: string) => Promise<void>;
  /** Exchanges a sign-in link for a session. Same result as `login`, different proof. */
  loginWithMagicLink: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
